import {
  type FC,
  useMemo,
  useRef,
  useState,
} from "react";

import { ComboboxCommand } from "@/ui/components/form/Combobox/ComboboxCommand";
import {
  COMBOBOX_DEFAULTS,
  type TComboboxOption,
  type TComboboxOptionGroup,
  type TComboboxOptions,
  type TComboboxProps,
  type TComboboxValue,
} from "@/ui/components/form/Combobox/Combobox.config";
import { ComboboxContent } from "@/ui/components/form/Combobox/ComboboxContent";
import { ComboboxEmpty } from "@/ui/components/form/Combobox/ComboboxEmpty";
import { ComboboxGroup } from "@/ui/components/form/Combobox/ComboboxGroup";
import { ComboboxInput } from "@/ui/components/form/Combobox/ComboboxInput";
import { ComboboxItem } from "@/ui/components/form/Combobox/ComboboxItem";
import { ComboboxList } from "@/ui/components/form/Combobox/ComboboxList";
import { ComboboxRoot } from "@/ui/components/form/Combobox/ComboboxRoot";
import { ComboboxTrigger } from "@/ui/components/form/Combobox/ComboboxTrigger";
import { Field } from "@/ui/components/form/Field";
import { Label } from "@/ui/components/form/Label";
import { Column } from "@/ui/components/layout/Column";
import { Grid } from "@/ui/components/layout/Grid";
import { cn } from "@/ui/lib/utils";

const isComboboxOptionGroup = (
  option: TComboboxOption | TComboboxOptionGroup,
): option is TComboboxOptionGroup => "options" in option;

const getComboboxOptionGroups = (
  options: TComboboxOptions,
): TComboboxOptionGroup[] => {
  const [ firstOption ] = options;

  if (firstOption && isComboboxOptionGroup(firstOption)) {
    return options as TComboboxOptionGroup[];
  }

  return [
    {
      label: "",
      options: options as TComboboxOption[],
    },
  ];
};

const getComboboxOptions = (
  optionGroups: TComboboxOptionGroup[],
): TComboboxOption[] => optionGroups.flatMap((optionGroup) => optionGroup.options);

const getComboboxSelectedValues = (
  value: TComboboxValue | undefined,
): string[] => {
  if (Array.isArray(value)) {
    return value;
  }

  return value ? [ value ] : [];
};

export const Combobox: FC<TComboboxProps> = (
  {
    className,
    contentClassName,
    disabled,
    emptyMessage = COMBOBOX_DEFAULTS.emptyMessage,
    error,
    id,
    inputClassName,
    isMultiSelect,
    label,
    hideLabel,
    onValueChange,
    options,
    placeholder = COMBOBOX_DEFAULTS.placeholder,
    searchPlaceholder = COMBOBOX_DEFAULTS.searchPlaceholder,
    triggerClassName,
    value,
    ...props
  },
) => {
  const [ open, setOpen ] = useState(false);
  const [ portalContainer, setPortalContainer ] = useState<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionGroups = useMemo(
    () => getComboboxOptionGroups(options),
    [ options ],
  );
  const flattenedOptions = useMemo(
    () => getComboboxOptions(optionGroups),
    [ optionGroups ],
  );
  const selectedValues = useMemo(
    () => getComboboxSelectedValues(value),
    [ value ],
  );
  const selectedLabel = useMemo(
    () => flattenedOptions
      .filter((option) => selectedValues.includes(option.value))
      .map((option) => option.label)
      .join(", "),
    [ flattenedOptions, selectedValues ],
  );
  const handleOptionSelect = (option: TComboboxOption) => {
    if (isMultiSelect) {
      const nextValue = selectedValues.includes(option.value)
        ? selectedValues.filter((selectedValue) => selectedValue !== option.value)
        : [ ...selectedValues, option.value ];

      onValueChange?.(nextValue);
      return;
    }

    onValueChange?.(option.value);
    setOpen(false);
  };
  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setPortalContainer(triggerRef.current?.closest<HTMLElement>(
        "[data-component=\"SheetContent\"]",
      ) ?? null);
    }

    setOpen(nextOpen);
  };

  return (
    <Field
      data-component="Combobox"
      className={cn(
        "cursor-pointer grid gap-x-2",
        disabled && "cursor-not-allowed",
        className,
      )}
    >
      { hideLabel ? (<span className="sr-only">{ label }</span>) : null }
      <Grid gap="3">
        { hideLabel ? (
          null
        ): (
          <Column>
            <Label>
              { label }
            </Label>
          </Column>
        ) }
        <Column>
          <ComboboxRoot
            {...props}
            open={open}
            onOpenChange={handleOpenChange}
          >
            <ComboboxTrigger
              ref={triggerRef}
              id={id}
              type="button"
              role="combobox"
              aria-expanded={open}
              aria-controls={open && id ? `${id}-listbox` : undefined}
              disabled={disabled}
              error={error}
              placeholder={placeholder}
              selectedLabel={selectedLabel || undefined}
              className={triggerClassName}
            />
            <ComboboxContent
              className={contentClassName}
              portalContainer={portalContainer}
            >
              <ComboboxCommand>
                <ComboboxInput
                  className={inputClassName}
                  placeholder={searchPlaceholder}
                />
                <ComboboxList
                  id={id ? `${id}-listbox` : undefined}
                  aria-multiselectable={isMultiSelect || undefined}
                >
                  <ComboboxEmpty>{ emptyMessage }</ComboboxEmpty>
                  { optionGroups.map((optionGroup) => (
                    <ComboboxGroup
                      key={optionGroup.label}
                      heading={optionGroup.label || undefined}
                    >
                      { optionGroup.options.map((option) => (
                        <ComboboxItem
                          key={option.value}
                          value={option.label}
                          disabled={option.disabled}
                          icon={option.icon}
                          isSelected={selectedValues.includes(option.value)}
                          onSelect={() => handleOptionSelect(option)}
                        >
                          { option.label }
                        </ComboboxItem>
                      )) }
                    </ComboboxGroup>
                  )) }
                </ComboboxList>
              </ComboboxCommand>
            </ComboboxContent>
          </ComboboxRoot>
        </Column>
      </Grid>
    </Field>
  );
};

Combobox.displayName = "Combobox";
